import apiService, { ApiError } from './apiService';

/**
 * Snap Earn Service
 * Handles all snap and earn transaction-related API operations
 */
class SnapEarnService {
  /**
   * Format date to API required format (YYYY-MM-DD HH:mm:ss)
   * @param {string|Date} date - Date to format
   * @param {boolean} isEndDate - Whether this is end date (adds 23:59:59)
   * @returns {string} Formatted date string
   */
  formatDateForAPI(date, isEndDate = false) {
    if (!date) return null;

    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return null;

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');

    const time = isEndDate ? '23:59:59' : '00:00:00';

    return `${year}-${month}-${day} ${time}`;
  }

  /**
   * Get list of snap and earn transactions with pagination, search, and filter
   * @param {Object} params - Filter and pagination parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.size - Items per page (default: 20)
   * @param {string} params.keyword - Search by company name or reference (optional)
   * @param {string} params.shopperName - Search by shopper name or phone (optional)
   * @param {Object} params.dateFilter - Date filter object (optional)
   * @param {string} params.dateFilter.start - Start date in 'YYYY-MM-DD HH:mm:ss' format
   * @param {string} params.dateFilter.end - End date in 'YYYY-MM-DD HH:mm:ss' format
   * @param {string} params.dateType - Date type: 'transactionDate' or 'submissionDate' (optional)
   * @param {string} params.status - Transaction status: A (Approved), P (Pending), R (Rejected) (optional)
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Transaction list response
   */
  async getTransactionList(params = {}, options = {}) {
    const { page = 1, size = 20, keyword, shopperName, dateFilter, dateType, status } = params;

    try {
      // Build request payload
      const requestData = {
        page,
        size,
      };

      if (keyword && keyword.trim()) {
        requestData.keyword = keyword.trim();
      }

      if (shopperName && shopperName.trim()) {
        requestData.shopperName = shopperName.trim();
      }

      if (status) {
        requestData.status = status;
      }

      if (dateFilter && dateFilter.start && dateFilter.end) {
        requestData.dateFilter = {
          start: dateFilter.start,
          end: dateFilter.end
        };

        if (dateType) {
          requestData.dateType = dateType;
        }
      }

      const response = await apiService.skaiOneRequest(
        '/api/v1/snap-earn/list',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      // Validate response structure
      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from snap earn transaction list API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch snap earn transaction list:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve transaction list',
        0,
        'SNAP_EARN_LIST_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Transform API transaction data to frontend format
   * @param {Object} apiTransaction - Raw transaction data from API
   * @returns {Object} Transformed transaction data
   */
  transformTransactionData(apiTransaction) {
    if (!apiTransaction) return null;

    // Map status code to display label
    const statusMap = {
      A: 'Approved',
      P: 'Pending',
      R: 'Rejected',
    };

    // Map files array to receiptImages (extract signed URLs)
    const receiptImages = (apiTransaction.files || [])
      .filter(file => file.fileType && file.fileType.startsWith('image/'))
      .map(file => file.fileName);

    return {
      id: apiTransaction.id,
      companyName: apiTransaction.companyName || 'N/A',
      sector: apiTransaction.sector || 'N/A',
      outlet: apiTransaction.outlet || 'N/A',
      // Current API documentation shows status as 'A', 'P', 'R'
      status:
        statusMap[apiTransaction.status] || apiTransaction.status || 'N/A',
      transactionAmount: apiTransaction.amount || 0,
      transactionDateTime: apiTransaction.transactionDate, // ISO string
      submissionDateTime: apiTransaction.submissionDate, // ISO string
      transactionDate: apiTransaction.transactionDate ? apiTransaction.transactionDate.split('T')[0] : '',
      transactionTime: apiTransaction.transactionDate ? apiTransaction.transactionDate.split('T')[1]?.substring(0, 5) : '',
      // Receipt images from files array
      receiptImages: receiptImages,
      // Raw files data for other file types if needed
      files: apiTransaction.files || [],
      // These are missing from the current API documentation but used in current UI dummy data
      shopperName: apiTransaction.shopperName || 'N/A',
      shopperPhone: apiTransaction.shopperPhone || 'N/A',
      rejectedReasonId: apiTransaction.rejectedReasonId,
      rejectedReasonDescription: apiTransaction.rejectedReasonDescription,
      histories: apiTransaction.histories || [],

    };
  }

  /**
   * Get formatted transaction list with transformed data
   * @param {Object} params - Filter and pagination parameters
   * @returns {Promise<Object>} Formatted transaction list response
   */
  async getFormattedTransactionList(params = {}, options = {}) {
    try {
      const response = await this.getTransactionList(params, options);

      // Transform the data array
      const transformedData = {
        ...response.data,
        data: (response.data.data || []).map(tx =>
          this.transformTransactionData(tx)
        ),
      };

      return {
        ...response,
        data: transformedData,
      };
    } catch (error) {
      console.error('Failed to get formatted transaction list:', error);
      throw error;
    }
  }

  /**
   * Get list of rejected reasons for Snap & Earn
   * This endpoint does not require authentication
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Rejected reasons list response
   */
  async getRejectedReasons(options = {}) {
    try {
      const response = await apiService.skaiOneRequest(
        '/api/v1/snap-earn/rejected-reasons',
        {
          method: 'GET',
          includeAuth: false, // No authentication required
          ...options,
        }
      );

      // Validate response structure
      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from rejected reasons API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch rejected reasons:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve rejected reasons',
        0,
        'REJECTED_REASONS_ERROR',
        { originalError: error.message }
      );
    }
  }

  /**
   * Get formatted rejected reasons list
   * @param {Object} options - Additional request options
   * @returns {Promise<Array>} Array of rejected reason objects with id and description
   */
  async getFormattedRejectedReasons(options = {}) {
    try {
      const response = await this.getRejectedReasons(options);

      // Return the data array directly
      return response.data || [];
    } catch (error) {
      console.error('Failed to get formatted rejected reasons:', error);
      throw error;
    }
  }
  /**
   * Process transaction approval (Approve/Reject)
   * @param {Object} data - Approval data
   * @param {string} data.snapEarnId - Transaction ID
   * @param {string} data.status - Status: 'A' (Approve) or 'R' (Reject)
   * @param {number} data.amount - Transaction amount
   * @param {string} [data.rejectedReasonId] - Reason ID (required if status is 'R')
   * @param {Object} options - Additional request options
   * @returns {Promise<Object>} Approval response
   */
  async processApproval(data, options = {}) {
    try {
      const response = await apiService.skaiOneRequest(
        '/api/v1/snap-earn/approval',
        {
          method: 'POST',
          data: data,
          ...options,
        }
      );

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from approval API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to process approval:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError('Failed to process approval', 0, 'APPROVAL_ERROR', {
        originalError: error.message,
      });
    }
  }
}

// Create singleton instance
const snapEarnService = new SnapEarnService();

export default snapEarnService;
export { SnapEarnService };
